import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, TableShell } from "@/components/EntityManager";
import { ListPagination } from "@/components/ListPagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, MessageCircle, Printer, Search, TrendingDown, TrendingUp } from "lucide-react";
import { inr, today, fmtDate } from "@/lib/format";
import { printRouteProfit } from "@/lib/print";
import { useAuth } from "@/context/AuthContext";
import { BRANCHES } from "@/lib/branches";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function RouteProfit() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [q, setQ] = useState("");
  const [branch, setBranch] = useState("All");
  const [sel, setSel] = useState(new Set());
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/reports/route-profit", { params: { from_date: from, to_date: to } }).then((r) => { setData(r.data); setSel(new Set()); }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [from, to, isAdmin]);
  const term = q.trim().toLowerCase();
  const rows = (data?.rows || [])
    .filter((r) => branch === "All" || (r.branch || "Chennai") === branch)
    .filter((r) => !term || [r.branch, r.from_place, r.destination, r.customer, r.lr_no, r.vehicle_no].filter(Boolean).some((v) => String(v).toLowerCase().includes(term)));
  const activeRows = sel.size ? rows.filter((_, i) => sel.has(i)) : rows;
  const activeTotals = {
    billing: activeRows.reduce((s, r) => s + r.billing, 0),
    hire: activeRows.reduce((s, r) => s + r.hire, 0),
    profit: activeRows.reduce((s, r) => s + r.profit, 0),
  };
  const losses = rows.filter((r) => r.profit < 0);
  const allSel = rows.length > 0 && rows.every((_, i) => sel.has(i));
  const toggleAll = () => setSel(allSel ? new Set() : new Set(rows.map((_, i) => i)));
  const toggleOne = (i) => setSel((s) => { const n = new Set(s); n.has(i) ? n.delete(i) : n.add(i); return n; });
  const RP_PER_PAGE = 20;
  const [rpPageNo, setRpPageNo] = useState(1);
  const rpPage = Math.min(rpPageNo, Math.max(1, Math.ceil(rows.length / RP_PER_PAGE)));
  const rpOffset = (rpPage - 1) * RP_PER_PAGE;
  const pagedRpRows = rows.slice(rpOffset, rpOffset + RP_PER_PAGE);

  const exportCsv = () => {
    const lines = ["Branch,LR No.,LR Date,From,Destination,Customer Name,Bills,Billing (Rs.),Hire Trips,Vehicle Hire (Rs.),Vehicle No.,Profit (Rs.),Margin %"];
    activeRows.forEach((r) => lines.push([r.branch, `"${r.lr_no || ""}"`, `"${r.lr_date || ""}"`, r.from_place, r.destination, `"${r.customer || ""}"`, r.bills, r.billing, r.trips, r.hire, `"${r.vehicle_no || ""}"`, r.profit, r.margin ?? ""].join(",")));
    lines.push(["TOTAL", "", "", "", "", "", "", activeTotals.billing, "", activeTotals.hire, "", activeTotals.profit, ""].join(","));
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `route-profit-${today()}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  };

  const fmtMonth = (m) => new Date(m + "-01").toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
  const shareWhatsApp = () => {
    const amt = (v) => `${v < 0 ? "-" : ""}₹${Math.abs(v).toLocaleString("en-IN")}`;
    const lines = activeRows.map((r) => {
      const t = (data.trend || []).find((x) => x.route === r.route)?.monthly || {};
      const mt = (data.months || []).map((m) => (t[m] === undefined ? null : `${fmtMonth(m)}: ${amt(t[m])}`)).filter(Boolean).join(" | ");
      return `${r.profit < 0 ? "🔴" : "🟢"} ${r.branch} · ${r.route}${r.customer ? ` · ${r.customer}` : ""} — Profit ${amt(r.profit)}${r.margin !== null && r.margin !== undefined ? ` (${r.margin}%)` : ""}${mt ? `\n   ${mt}` : ""}`;
    });
    const msg = `*NISHA TRANSPORT CORPORATION*\n*ROUTE PROFIT — MONTHLY TREND*\nPeriod: ${from || "Start"} to ${to || "Today"}\n\n${lines.join("\n")}\n\nTOTAL — Billing ₹${activeTotals.billing.toLocaleString("en-IN")} | Hire ₹${activeTotals.hire.toLocaleString("en-IN")} | Profit ${amt(activeTotals.profit)}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div>
      {!isAdmin ? (
        <div className="rounded-lg border bg-white py-16 text-center text-muted-foreground" data-testid="route-profit-admin-only">
          Route Profit is available to admin login only.
        </div>
      ) : (
      <>
      <PageHeader
        title="Route Profit"
        subtitle="Billing vs vehicle-hire cost per route and customer — loss-making lanes rise to the top."
        action={
          <span className="flex gap-2">
            <Button variant="outline" className="text-emerald-600" onClick={shareWhatsApp} disabled={!activeRows.length} data-testid="route-profit-whatsapp-btn">
              <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp{sel.size ? ` (${activeRows.length})` : ""}
            </Button>
            <Button variant="outline" onClick={() => printRouteProfit({ rows: activeRows, totals: activeTotals, from, to })} disabled={!activeRows.length} data-testid="route-profit-print-btn">
              <Printer className="mr-2 h-4 w-4" /> Print{sel.size ? ` (${activeRows.length})` : ""}
            </Button>
            <Button variant="outline" onClick={exportCsv} disabled={!activeRows.length} data-testid="route-profit-excel-btn">
              <Download className="mr-2 h-4 w-4" /> Excel
            </Button>
          </span>
        }
      />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="grid gap-1">
          <Label className="text-xs">From Date</Label>
          <Input type="date" className="h-9 w-[160px] bg-white" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="route-profit-from" />
        </div>
        <div className="grid gap-1">
          <Label className="text-xs">To Date</Label>
          <Input type="date" className="h-9 w-[160px] bg-white" value={to} onChange={(e) => setTo(e.target.value)} data-testid="route-profit-to" />
        </div>
        <div className="grid gap-1">
          <Label className="text-xs">Search</Label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input className="h-9 w-[240px] bg-white pl-8" placeholder="Branch, place or customer…" value={q} onChange={(e) => { setQ(e.target.value); setSel(new Set()); }} data-testid="route-profit-search" />
          </div>
        </div>
        <div className="grid gap-1">
          <Label className="text-xs">Branch</Label>
          <Select value={branch} onValueChange={(v) => { setBranch(v); setSel(new Set()); }}>
            <SelectTrigger className="h-9 w-[160px] bg-white" data-testid="route-profit-branch">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {["All", ...BRANCHES].map((b) => (
                <SelectItem key={b} value={b} data-testid={`route-profit-branch-${b.toLowerCase()}`}>{b === "All" ? "All Branches" : b}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {(from || to || q || branch !== "All") && (
          <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={() => { setFrom(""); setTo(""); setQ(""); setBranch("All"); setSel(new Set()); }} data-testid="route-profit-clear">
            Clear
          </Button>
        )}
      </div>
      {losses.length > 0 && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-300 bg-red-50/70 px-4 py-3 text-sm font-bold text-red-800" data-testid="route-loss-alert">
          <TrendingDown className="h-4 w-4" /> {losses.length} loss-making lane{losses.length > 1 ? "s" : ""} — hire cost exceeds billing
        </div>
      )}
      <TableShell>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="w-10"><Checkbox checked={allSel} onCheckedChange={toggleAll} data-testid="route-profit-select-all" /></TableHead>
              {["Branch", "LR No.", "LR Date", "From", "Destination", "Customer Name", "Bills", "Billing (₹)", "Hire Trips", "Vehicle Hire (₹)", "Vehicle No.", "Profit (₹)", "Margin %"].map((h, i) => (
                <TableHead key={h} className={`text-[11px] uppercase tracking-[0.15em] text-slate-500 ${[6, 7, 8, 9, 11, 12].includes(i) ? "text-right" : ""}`}>{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data && rows.length === 0 && (
              <TableRow><TableCell colSpan={14} className="py-12 text-center text-muted-foreground">No routes found — add From/Destination on bills and challans.</TableCell></TableRow>
            )}
            {pagedRpRows.map((r, pi) => {
              const i = rpOffset + pi;
              return (
              <TableRow key={`${r.branch}-${r.route}-${r.customer}`} className={r.profit < 0 ? "bg-red-50/60 hover:bg-red-50" : ""} data-testid="route-profit-row">
                <TableCell><Checkbox checked={sel.has(i)} onCheckedChange={() => toggleOne(i)} data-testid="route-profit-select-row" /></TableCell>
                <TableCell className="text-sm">{r.branch}</TableCell>
                <TableCell className="text-sm" data-testid="route-profit-lrno">{r.lr_no || "—"}</TableCell>
                <TableCell className="text-sm whitespace-nowrap" data-testid="route-profit-lrdate">{r.lr_date ? r.lr_date.split(", ").map((d) => fmtDate(d)).join(", ") : "—"}</TableCell>
                <TableCell className="text-sm font-semibold">{r.from_place}</TableCell>
                <TableCell className="text-sm font-semibold">
                  {r.destination}
                  {r.profit < 0 && <span className="ml-2 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold text-white" data-testid="route-loss-badge">LOSS</span>}
                </TableCell>
                <TableCell className="text-sm" data-testid="route-profit-customer">{r.customer || "—"}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{r.bills}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{inr(r.billing)}</TableCell>
                <TableCell className="text-right text-sm tabular-nums">{r.trips}</TableCell>
                <TableCell className="text-right text-sm tabular-nums text-amber-700">{inr(r.hire)}</TableCell>
                <TableCell className="text-sm" data-testid="route-profit-vehicleno">{r.vehicle_no || "—"}</TableCell>
                <TableCell className={`text-right text-sm font-bold tabular-nums ${r.profit < 0 ? "text-red-600" : "text-emerald-700"}`} data-testid="route-profit-cell">
                  {r.profit < 0 ? `-${inr(Math.abs(r.profit))}` : inr(r.profit)}
                </TableCell>
                <TableCell className={`text-right text-sm tabular-nums ${r.profit < 0 ? "text-red-600" : "text-slate-600"}`}>
                  {r.margin === null || r.margin === undefined ? "—" : `${r.margin}%`}
                </TableCell>
              </TableRow>
              );
            })}
            {data && rows.length > 0 && (
              <TableRow className="bg-slate-100 font-bold" data-testid="route-profit-total">
                <TableCell />
                <TableCell colSpan={6}><span className="flex items-center gap-1.5"><TrendingUp className="h-4 w-4 text-emerald-600" /> TOTAL{sel.size ? ` (${activeRows.length} selected)` : ""}</span></TableCell>
                <TableCell className="text-right tabular-nums">{activeRows.reduce((s, r) => s + r.bills, 0)}</TableCell>
                <TableCell className="text-right tabular-nums">{inr(activeTotals.billing)}</TableCell>
                <TableCell className="text-right tabular-nums">{activeRows.reduce((s, r) => s + r.trips, 0)}</TableCell>
                <TableCell className="text-right tabular-nums text-amber-700">{inr(activeTotals.hire)}</TableCell>
                <TableCell />
                <TableCell className={`text-right tabular-nums ${activeTotals.profit < 0 ? "text-red-600" : "text-emerald-700"}`}>
                  {activeTotals.profit < 0 ? `-${inr(Math.abs(activeTotals.profit))}` : inr(activeTotals.profit)}
                </TableCell>
                <TableCell />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableShell>
      <ListPagination total={rows.length} page={rpPageNo} setPage={setRpPageNo} pageSize={RP_PER_PAGE} noun="routes" testPrefix="routeprofit" />
      {data?.months?.length > 0 && rows.length > 0 && (
        <div className="mt-6">
          <h3 className="mb-2 font-display text-lg font-semibold">Monthly Profit Trend</h3>
          <p className="mb-3 text-xs text-muted-foreground">Profit per route month by month — red cells show months the lane slipped into loss.</p>
          <TableShell>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="text-[11px] uppercase tracking-[0.15em] text-slate-500">Route</TableHead>
                  {data.months.map((m) => (
                    <TableHead key={m} className="text-right text-[11px] uppercase tracking-[0.15em] text-slate-500">{fmtMonth(m)}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.trend.map((t) => (
                  <TableRow key={t.route} data-testid="route-trend-row">
                    <TableCell className="text-sm font-semibold">{t.route}</TableCell>
                    {data.months.map((m) => {
                      const p = t.monthly[m];
                      return (
                        <TableCell key={m} className={`text-right text-sm font-semibold tabular-nums ${p === undefined ? "text-slate-300" : p < 0 ? "bg-red-50 text-red-600" : "text-emerald-700"}`} data-testid="route-trend-cell">
                          {p === undefined ? "—" : p < 0 ? `-${inr(Math.abs(p))}` : inr(p)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableShell>
        </div>
      )}
      {data?.customer_rank?.length > 0 && (
        <div className="mt-6 grid gap-4 md:grid-cols-2" data-testid="customer-profit-rank">
          {[
            { title: "Top 10 Most Profitable Customers", list: data.customer_rank.slice(0, 10), testid: "rank-most" },
            { title: "Top 10 Least Profitable Customers", list: [...data.customer_rank].slice(-10).reverse(), testid: "rank-least" },
          ].map((sec) => (
            <div key={sec.title}>
              <h3 className="mb-2 font-display text-lg font-semibold">{sec.title}</h3>
              <TableShell>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                      {["#", "Customer", "Billing (₹)", "Hire Share (₹)", "Profit (₹)", "Margin %"].map((h, i) => (
                        <TableHead key={h} className={`text-[11px] uppercase tracking-[0.12em] text-slate-500 ${i > 1 ? "text-right" : ""}`}>{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sec.list.map((g, i) => (
                      <TableRow key={g.customer} className={g.profit < 0 ? "bg-red-50/60 hover:bg-red-50" : ""} data-testid={`${sec.testid}-row`}>
                        <TableCell className="text-sm text-muted-foreground">{i + 1}</TableCell>
                        <TableCell className="text-sm font-semibold">{g.customer}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums">{inr(g.billing)}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums text-amber-700">{inr(g.hire_share)}</TableCell>
                        <TableCell className={`text-right text-sm font-bold tabular-nums ${g.profit < 0 ? "text-red-600" : "text-emerald-700"}`}>
                          {g.profit < 0 ? `-${inr(Math.abs(g.profit))}` : inr(g.profit)}
                        </TableCell>
                        <TableCell className={`text-right text-sm tabular-nums ${g.profit < 0 ? "text-red-600" : "text-slate-600"}`}>
                          {g.margin === null || g.margin === undefined ? "—" : `${g.margin}%`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableShell>
            </div>
          ))}
        </div>
      )}
      </>
      )}
    </div>
  );
}
